import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { BsCloudDownload } from "react-icons/bs";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { GalleryContainer } from "../../styled/scheduler";
import useOutsideClick from "../../../../../../hooks/useOutsideClick";
import { useMediaQuery } from "react-responsive";
import { IoIosMore } from "react-icons/io";
import moment from "moment/moment";

const Gallery = ({
  eventInfo,
  setImageOrder,
  setIsDetail,
  isGallery,
  originImages,
  getImages,
  clickGallery,
  isUpload,
  isProject = false,
  getComment,
}) => {
  const contextRef = useRef();

  const user = useSelector((state) => state?.user?.data[0]);
  const { isDarkMode } = useSelector((state) => state.scheduler);

  const [selectedImage, setSelectedImage] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [files, setFiles] = useState([]);
  const [images, setImages] = useState([]);

  useEffect(() => {
    setImages([...originImages]);
  }, [originImages]);

  const isRelatedPerson =
    user &&
    ((eventInfo?.gbn === "P" && String(eventInfo?.mberNo) === String(user.mberNo)) ||
      eventInfo?.managerNo === user.mberNo ||
      user.mberNo in (eventInfo?.workerList ?? {}));

  useOutsideClick(contextRef, () => {
    setSelectedImage(null);
  });

  // 이미지 삭제
  const deleteImages = async (item) => {
    const isEncodedImage = item.path.startsWith("data:image");
    const image = item.path;

    if (isEncodedImage) {
      setImages(images.filter((img) => img.path !== image));
      setFiles(files.filter((img) => img.path !== image));
    } else {
      const url = "/api/photoDelete";
      const body = { photoNos: item.photoNo };
      const res = await axios.post(url, body);
      if (res.data.length) {
        await getImages();
        await getComment();
      }
    }

    toast.success("이미지가 삭제되었습니다.");
    setSelectedImage(null);
  };

  // 이미지 업로드
  const uploadImages = async (isCloseModal = false) => {
    if (!files.length) {
      toast.error("업로드 할 이미지가 존재하지 않습니다.");
      return;
    }

    try {
      const formData = new FormData();
      files.forEach((image) => formData.append("images", image));

      if (eventInfo.projectNo) {
        formData.append("gbn", "P");
      } else {
        formData.append("gbn", "S");
      }
      formData.append("refeNo", eventInfo.scheduleNo);
      formData.append("mberNo", user.mberNo);

      const response = await axios.post("/api/photos", formData, {});

      if (response.status === 200) {
        toast.success("이미지가 성공적으로 업로드되었습니다.");
        if (isCloseModal) {
          setImages([]);
          setShowEvent(false);
        } else {
          getImages();
        }
        setFiles([]);
      } else {
        console.error("업로드 실패", response.data);
      }
    } catch (error) {
      console.error("업로드 중 에러 발생", error);
    } finally {
      // closeModal();
    }
  };

  // 파일 추가
  const onFilesAdded = useCallback((files) => {
    if (!isRelatedPerson) {
      toast.error("이미지 업로드 권한이 없습니다.");
      return;
    }

    if (files.length) {
      const imgArr = [];
      let loadedCount = 0;

      for (let i = 0; i < files.length; i++) {
        const reader = new FileReader();

        reader.onload = (event) => {
          imgArr[i] = { path: event.target.result };
          loadedCount++;
          files[i].path = event.target.result;

          if (loadedCount === files.length) {
            setFiles((prev) => [...files, ...prev]);
            setImages((prev) => [...imgArr.filter(Boolean), ...prev]);
          }
        };
        reader.readAsDataURL(files[i]);
      }
    }
  }, []);

  const handleDrag = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      const files = event.dataTransfer.files;
      onFilesAdded(files);
    },
    [onFilesAdded],
  );

  const handleFileChange = useCallback(
    (event) => {
      const files = event.target.files;
      onFilesAdded(files);
    },
    [onFilesAdded],
  );

  const handleContextMenu = (e, item) => {
    e.preventDefault();

    const ulElement = e.currentTarget.closest("ul");
    const rect = ulElement.getBoundingClientRect();

    setPosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });

    setSelectedImage(item);
  };
  const downloadImage = async (image) => {
    if (!image) return;

    if (image.startsWith("data:image")) {
      const a = document.createElement("a");
      a.href = image;
      a.download = "image.png";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("이미지가 저장되었습니다.");
      setSelectedImage(null);
      return;
    }

    try {
      const absoluteUrl = image.startsWith("http") ? image : imgHandler(image);
      const res = await axios.post("/api/getFile", { url: absoluteUrl }, { responseType: "blob" });

      const blob = new Blob([res.data], {
        type: res.headers["content-type"] || "image/png",
      });

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = decodeURIComponent(absoluteUrl.split("/").pop() || "image.png");
      a.click();
      window.URL.revokeObjectURL(blobUrl);

      toast.success("이미지가 저장되었습니다.");
    } catch (error) {
      console.error("이미지 다운로드 실패:", error);
      toast.error("이미지 저장에 실패하였습니다.");
    } finally {
      setSelectedImage(null);
    }
  };

  const handleImageClick = (index) => {
    const clickedImage = [...images][index];

    if (!clickedImage || clickedImage.path.startsWith("data:image")) return;

    const allValidImages = [...images]
      .reverse()
      .filter((img) => !img.path.startsWith("data:image"));
    const validIndex = allValidImages.indexOf(clickedImage);

    const reorderedImages = [
      ...allValidImages.slice(validIndex),
      ...allValidImages.slice(0, validIndex),
    ];

    setImageOrder(reorderedImages);
    setIsDetail(true);
  };

  return (
    <GalleryContainer
      className={`gallery ${isGallery && isUpload ? "upload" : "notUpload"} ${
        isProject ? "" : isDarkMode
      }`}>
      {isGallery && selectedImage?.photoNo && (
        <ul className="context" style={{ top: position.y, left: position.x }} ref={contextRef}>
          <li onClick={() => downloadImage(selectedImage.path)}>이미지 저장</li>
          {isRelatedPerson && <li onClick={() => deleteImages(selectedImage)}>삭제</li>}
        </ul>
      )}
      {!isGallery && (
        <div className="label">
          <span>갤러리</span>
        </div>
      )}
      <ul className={`images${isGallery ? " gallery" : ""}`}>
        {images.length > 0 ? (
          (isGallery ? [...images] : [...images].slice(0, 3)).map((image, index) => (
            <li
              onContextMenu={(e) => handleContextMenu(e, image)}
              onClick={() => handleImageClick(index)}>
              <img
                src={imgHandler(image.path)}
                loading="lazy"
                decoding="async"
              />
              <p>
                <strong>{images.length - index}</strong>
                {<span>{image.lastComment}</span>}
              </p>
            </li>
          ))
        ) : (
          <li className="not">이미지가 존재하지 않습니다.</li>
        )}
        {!isGallery && images.length > 3 && (
          <li className="more">
            <button onClick={() => clickGallery(false)}>
              <IoIosMore />
            </button>
          </li>
        )}
      </ul>
      {isGallery && isUpload && (
        <>
          <div className="dragToUpload">
            <input
              type="file"
              id="fileUpload"
              style={{ display: "none" }}
              onChange={handleFileChange}
              accept="image/*"
              multiple
            />
            <label
              htmlFor="fileUpload"
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}>
              <i>
                <BsCloudDownload />
              </i>
              <span>이미지를 여기로 드래그하거나 클릭해서 추가해주세요.</span>
            </label>
          </div>
          <button className="submit" onClick={() => uploadImages()}>
            저장
          </button>
        </>
      )}
    </GalleryContainer>
  );
};

export default Gallery;
